// Cite index (DESIGN_SPEC §12.6): which events each behavior tag points at, so completed
// documents can draw severity edges and technique ids on the rows the rules matched.
// Best-effort matching of a tag's evidence strings against event fields:
//   path / image          file.*, image.load, proc.start (case-insensitive, %APPDATA%-normalized)
//   registry key          registry.set / registry.delete
//   creator=X target=Y    thread.inject
//   Rule @ artifact       yara.match
//   host · host/path · URL net.dns (query or answer name), net.http
//   any other text        a substring of a proc.start command line; mutex and dump names
// The most severe citing tag wins. Live documents never use this: no severities before scoring.
//   citeIndex(tags, events) → Map<seq, { sev, mitre?, tagId, tagIds }>
import { envPath } from "../format";
import type { Event, Tag } from "@/types";
import type { CiteEntry, CiteIndex } from "./types";

type Matchers = {
  paths: Set<string>;
  keys: Set<string>;
  injects: Set<string>;
  yara: Set<string>;
  hosts: Set<string>;
  hostPaths: string[];
  texts: string[];
  names: Set<string>;
};

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const normPath = (p: string) => envPath(p.trim().replace(/\//g, "\\")).toLowerCase().replace(/\\+$/, "");
const normText = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();

const PATH_RE = /^([a-z]:\\|%[a-z_]+%\\|\\\\[^\\])/i;
const REG_RE = /^(hkey_|hk(cu|lm|cr|u|cc)\\|\\registry\\)/i;
const HOSTISH_RE = /^[a-z0-9._-]+\.[a-z0-9-]+(\/\S*)?$/i;

const HIVES: [RegExp, string][] = [
  [/^hkey_current_user(?=\\|$)/, "hkcu"],
  [/^hkey_local_machine(?=\\|$)/, "hklm"],
  [/^hkey_classes_root(?=\\|$)/, "hkcr"],
  [/^hkey_users(?=\\|$)/, "hku"],
  [/^hkey_current_config(?=\\|$)/, "hkcc"],
  [/^\\registry\\machine(?=\\|$)/, "hklm"],
];

function normKey(k: string): string {
  const low = k.trim().toLowerCase().replace(/\\+$/, "");
  for (const [re, short] of HIVES) if (re.test(low)) return low.replace(re, short);
  return low;
}

function matchersFor(tag: Tag): Matchers {
  const m: Matchers = { paths: new Set(), keys: new Set(), injects: new Set(), yara: new Set(), hosts: new Set(), hostPaths: [], texts: [], names: new Set() };
  for (const ev of tag.evidence ?? []) {
    const raw = ev.trim();
    if (!raw) continue;
    const low = raw.toLowerCase();
    const inj = /creator=(\d+)\s+target=(\d+)/i.exec(raw);
    if (inj) {
      m.injects.add(`${inj[1]}>${inj[2]}`);
      continue;
    }
    const y = /^(\S.*?)\s+@\s+(\S.*)$/.exec(raw);
    if (y) {
      m.yara.add(`${y[1].toLowerCase()}@${y[2].toLowerCase()}`);
      continue;
    }
    if (PATH_RE.test(raw)) {
      m.paths.add(normPath(raw));
      continue;
    }
    if (REG_RE.test(raw)) {
      m.keys.add(normKey(raw));
      continue;
    }
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
      try {
        const u = new URL(raw);
        m.hostPaths.push(`${u.hostname}${u.pathname}`.toLowerCase());
      } catch {
        /* not a URL after all */
      }
      continue;
    }
    if (HOSTISH_RE.test(raw)) {
      // A dotted token is a host, or a bare file name (a dump, an image): keep both readings.
      const slash = low.indexOf("/");
      if (slash > 0) m.hostPaths.push(low);
      else m.hosts.add(low.replace(/\.$/, ""));
      m.names.add(low);
      continue;
    }
    m.names.add(low);
    if (raw.length >= 8) m.texts.push(normText(raw));
  }
  return m;
}

function cites(m: Matchers, e: Event): boolean {
  const f = e.fields;
  const path = (v: unknown) => m.paths.size > 0 && !!str(v) && m.paths.has(normPath(str(v)));
  switch (e.kind) {
    case "file.create":
    case "file.write":
    case "file.delete":
    case "file.drop":
      return path(f.path);
    case "file.rename":
      return path(f.from) || path(f.to) || path(f.path);
    case "image.load":
      return path(f.image);
    case "proc.start": {
      if (path(f.image)) return true;
      const cmd = normText(str(f.cmdline));
      return !!cmd && m.texts.some((t) => cmd.includes(t));
    }
    case "registry.set":
    case "registry.delete": {
      if (m.keys.size === 0) return false;
      const key = normKey(str(f.key));
      if (!key) return false;
      const valueName = str(f.name ?? f.value_name).toLowerCase();
      return m.keys.has(key) || (!!valueName && m.keys.has(`${key}\\${valueName}`));
    }
    case "thread.inject":
      return m.injects.has(`${str(f.creator_pid)}>${str(f.target_pid)}`);
    case "yara.match":
      return m.yara.has(`${str(f.rule).toLowerCase()}@${str(f.artifact).toLowerCase()}`);
    case "net.dns": {
      const q = str(f.qname ?? f.name).toLowerCase().replace(/\.$/, "");
      return !!q && m.hosts.has(q);
    }
    case "net.http": {
      const host = str(f.host).toLowerCase().replace(/:\d+$/, "");
      if (!host) return false;
      if (m.hosts.has(host)) return true;
      const hp = `${host}${str(f.path).toLowerCase()}`;
      return m.hostPaths.some((p) => hp === p || hp.startsWith(p));
    }
    case "mutex.create":
    case "dump.taken":
      return m.names.has(str(f.name).toLowerCase());
    default:
      return false;
  }
}

export function citeIndex(tags: readonly Tag[] | null | undefined, events: readonly Event[]): CiteIndex {
  const out = new Map<number, CiteEntry>();
  if (!tags?.length) return out;
  // Most severe first (stable), so the first citing tag is the winner.
  const ranked = tags
    .map((tag, i) => ({ tag, i, m: matchersFor(tag) }))
    .sort((a, b) => b.tag.severity - a.tag.severity || a.i - b.i);
  for (const e of events) {
    let entry: CiteEntry | undefined;
    for (const { tag, m } of ranked) {
      if (!cites(m, e)) continue;
      if (!entry) entry = { sev: tag.severity, mitre: tag.mitre_ids?.[0], tagId: tag.id, tagIds: [tag.id] };
      else entry.tagIds.push(tag.id);
    }
    if (entry) out.set(e.seq, entry);
  }
  return out;
}
